import React from "react";
import { Handle, Position } from "@xyflow/react";
import { X, Sparkles } from "lucide-react";
import { getNodeRegistryEntry } from "../nodeRegistry";
import { useCanvasContext } from "../WorkflowCanvasContext";

const STATE_STYLES = {
    default: "border-gray-200",
    selected: "border-blue-500 shadow-[0_0_12px_rgba(59,130,246,0.15)]",
    running: "border-blue-400 shadow-[0_0_12px_rgba(59,130,246,0.2)] animate-pulse",
    success: "border-green-300",
    error: "border-red-400 shadow-[0_0_8px_rgba(239,68,68,0.15)]",
    disabled: "opacity-50 border-gray-200",
};

function CanvasNode({ id, data, selected }) {
    const { clearStepNotes } = useCanvasContext();
    const iconKey = data.icon || "default";
    const entry = getNodeRegistryEntry(iconKey);
    const Icon = entry.icon;
    const execStatus = data.execution?.status;
    const state = execStatus || (selected ? "selected" : "default");
    const borderClass = STATE_STYLES[state] || STATE_STYLES.default;
    const result = data.execution?.result;

    const itemCount = Array.isArray(result?.output || result?.result) ? (result.output || result.result).length : result ? 1 : null;
    const duration = result?.duration || null;
    const extractError = (val) => {
        if (val == null) return null;
        if (typeof val === "string") return val;
        if (typeof val === "object") return val.error || val.message || null;
        return String(val);
    };
    const errorMsg = execStatus === "error"
        ? (extractError(result?.message) || extractError(result?.error) || "Error")
        : null;

    return (
        <div className="flex flex-col items-center min-w-[220px] max-w-[220px]">
            <div className={`bg-white rounded-lg border-2 ${borderClass} px-3 py-2 w-full cursor-pointer transition-all duration-200 hover:border-gray-300 shadow-sm`}>
                <Handle type="target" position={Position.Left} className="!bg-gray-300 !w-2 !h-2 !border-0" />
                <div className="flex items-center gap-2">
                    <div className="w-7 h-7 rounded flex items-center justify-center flex-shrink-0" style={{ backgroundColor: entry.color + "15" }}>
                        <Icon size={15} style={{ color: entry.color }} />
                    </div>
                    <div className="min-w-0 flex-1">
                        <div className="text-xs font-medium text-gray-900 truncate">{data.title || data.label || "Untitled"}</div>
                        {data.subtitle && <div className="text-[10px] text-gray-500 truncate">{data.subtitle}</div>}
                    </div>
                    {execStatus === "success" && <div className="w-2 h-2 rounded-full bg-green-500 flex-shrink-0" />}
                    {execStatus === "error" && <div className="w-2 h-2 rounded-full bg-red-500 flex-shrink-0" />}
                    {execStatus === "running" && <div className="w-2 h-2 rounded-full bg-blue-500 animate-pulse flex-shrink-0" />}
                </div>
                {execStatus === "success" && itemCount != null && (
                    <div className="text-[10px] text-green-600 mt-1">
                        {itemCount} item{itemCount !== 1 ? "s" : ""}{duration ? ` · ${duration}ms` : ""}
                    </div>
                )}
                {execStatus === "error" && errorMsg && (
                    <div className="text-[10px] text-red-500 mt-1 truncate">{errorMsg}</div>
                )}
                <Handle type="source" position={Position.Right} className="!bg-gray-300 !w-2 !h-2 !border-0" />
            </div>
            {data.notes && (
                <div className="mt-1.5 bg-purple-50 border border-purple-200 rounded px-2 py-1.5 w-full shadow-sm group/notes relative">
                    <div className="flex items-start gap-1">
                        <Sparkles size={10} className="text-purple-400 flex-shrink-0 mt-0.5" />
                        <div className="text-[10px] text-purple-700 leading-tight">{data.notes}</div>
                    </div>
                    <button onClick={(e) => { e.stopPropagation(); clearStepNotes(id); }}
                        className="absolute -top-1.5 -right-1.5 hidden group-hover/notes:flex w-4 h-4 items-center justify-center rounded-full bg-purple-200 hover:bg-purple-300 text-purple-600">
                        <X size={9} />
                    </button>
                </div>
            )}
        </div>
    );
}

export default React.memo(CanvasNode);
